import { useEffect, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useGetSupportContextQuery } from "src/store/services/privateApi";

const SUPPORT_SCRIPT_ID = "rex-support-embed";
const SUPPORT_SCRIPT_SRC = "https://rex-support-os.vercel.app/embed.js";
const SUPPORT_ORIGIN = "https://rex-support-os.vercel.app";

declare global {
  interface Window {
    RexSupport?: {
      user: { email: string; name: string; id: string };
      // Signed resident-context token, minted by resident-portal-api from the
      // resident's active lease. embed.js forwards it to the chatbot so it can
      // read/create GetDone work orders scoped to this resident's unit.
      token?: string;
      // Installed by embed.js. Pushes a newly minted token straight into the
      // already-loaded widget iframe.
      refreshToken?: (token: string) => void;
    };
  }
}

// The token's TTL is 60m server-side. Poll well inside that so a conversation
// that runs long never crosses the expiry mid-chat, which is exactly what
// happened on 2026-07-27: a work-order lookup succeeded and the identical call
// failed 109 seconds later with nothing changed but the clock.
const TOKEN_REFRESH_MS = 15 * 60 * 1000;

export default function RexSupportWidget() {
  const { user, isAuthenticated } = useAuth0();
  // Fetch the signed context token. Skip until authenticated. The chatbot still
  // loads (and answers KB questions) if this fails — it just can't touch work
  // orders without the token.
  const {
    data: supportContext,
    isSuccess,
    isError,
    refetch,
  } = useGetSupportContextQuery(undefined, {
    skip: !isAuthenticated,
    // Backstop for conversations long enough to outlive a token. The primary
    // mechanism is the refetch-on-open below. (RTK 1.8 has no
    // skipPollingIfUnfocused, and setupListeners is not configured in this app,
    // so refetchOnFocus would be a silent no-op — don't add it here.)
    pollingInterval: TOKEN_REFRESH_MS,
  });

  const [hasSupportContextSettled, setHasSupportContextSettled] =
    useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      setHasSupportContextSettled(false);
      return;
    }
    if (isSuccess || isError) {
      setHasSupportContextSettled(true);
    }
  }, [isAuthenticated, isSuccess, isError]);

  // Re-mint the moment the resident opens the chat. This is the case that was
  // actually breaking: someone idles in the portal, then opens chat holding a
  // token minted at page load. embed.js relays the widget's open/closed events to
  // the parent window, so we get an exact signal instead of guessing.
  useEffect(() => {
    if (!isAuthenticated) return;
    function onWidgetMessage(e: MessageEvent) {
      if (e.origin !== SUPPORT_ORIGIN) return;
      const d = e.data;
      if (!d || d.source !== "rex-support-widget" || d.type !== "open") return;
      refetch();
    }
    window.addEventListener("message", onWidgetMessage);
    return () => window.removeEventListener("message", onWidgetMessage);
  }, [isAuthenticated, refetch]);

  // Latest token, read by the mount effect without being a dependency of it.
  const tokenRef = useRef<string | undefined>(undefined);
  tokenRef.current = supportContext?.token;

  // Mount the widget exactly once per session. This effect deliberately does NOT
  // depend on the token: its cleanup removes the script and deletes
  // window.RexSupport, so re-running it tears the widget down and wipes the
  // resident's in-progress conversation. Token changes are handled separately
  // below, which is what makes polling safe.
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    // Wait for the query to settle before injecting embed.js so the token (if
    // any) is set on window before the script reads it on first load. Load the
    // widget even when the query fails or returns no token — it still answers
    // KB questions, it just can't touch work orders.
    if (!hasSupportContextSettled) return;

    window.RexSupport = {
      ...window.RexSupport,
      user: {
        email: user.email ?? "",
        name: user.name ?? "",
        id: user.sub ?? "",
      },
    };
    if (tokenRef.current) {
      window.RexSupport.token = tokenRef.current;
    }

    if (!document.getElementById(SUPPORT_SCRIPT_ID)) {
      const script = document.createElement("script");
      script.id = SUPPORT_SCRIPT_ID;
      script.src = SUPPORT_SCRIPT_SRC;
      script.defer = true;
      script.dataset.rexSupportProduct = "tenantport";
      script.dataset.rexSupportBottom = "70";
      document.head.appendChild(script);
    }

    return () => {
      document.getElementById(SUPPORT_SCRIPT_ID)?.remove();
      delete window.RexSupport;
    };
  }, [isAuthenticated, user, hasSupportContextSettled]);

  // Push each newly minted token into the already-loaded widget. refreshToken is
  // installed by embed.js; assigning the field is the fallback for the window
  // between script injection and execution, which embed.js also watches for.
  useEffect(() => {
    const token = supportContext?.token;
    if (!token || !window.RexSupport) return;
    if (window.RexSupport.refreshToken) window.RexSupport.refreshToken(token);
    else window.RexSupport.token = token;
  }, [supportContext?.token]);

  return null;
}
